// src/config/apiConfig.js

// ✅ LOCAL/PRODUCTION CONFIGURATION
// LIVE (PRODUCTION) is the default - use local only if explicitly set
// Set REACT_APP_USE_LOCAL_BACKEND=true or use npm run start:local to use local backend

const LOCAL_API_BASE_URL = "http://localhost:3001/api";
const PRODUCTION_API_BASE_URL = "https://dreamyapps-api-ahe2cpb3hfewanbb.uksouth-01.azurewebsites.net/api";

// 🔥 USE LOCAL BACKEND IN DEVELOPMENT - Use production only if explicitly set
// For production builds, ALWAYS use production API (REACT_APP_USE_LOCAL_BACKEND=false)
// For local development, default to localhost API
const isProduction = process.env.NODE_ENV === 'production';
const isLocalhost = typeof window !== 'undefined' && 
                    (window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1');
const isDevelopment = !isProduction && isLocalhost;

// In production builds, NEVER use local backend (even if REACT_APP_USE_LOCAL_BACKEND is set)
// Only use local backend if explicitly set to 'true' AND we're in development
const useLocalBackend = !isProduction && 
                        (process.env.REACT_APP_USE_LOCAL_BACKEND === 'true' || 
                         (isDevelopment && process.env.REACT_APP_USE_LOCAL_BACKEND !== 'false'));

const API_BASE_URL = useLocalBackend 
  ? LOCAL_API_BASE_URL 
  : PRODUCTION_API_BASE_URL;

// Debug logging
console.log('🔍 [API Config] Environment:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_USE_LOCAL_BACKEND: process.env.REACT_APP_USE_LOCAL_BACKEND,
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
  API_BASE_URL,
  isLocal: API_BASE_URL.includes('localhost'),
  useLocalBackend
});

if (API_BASE_URL.includes('localhost')) {
  console.log('✅ [API Config] Using LOCAL backend:', API_BASE_URL);
} else {
  console.log('☁️  [API Config] Using LIVE/PRODUCTION backend:', API_BASE_URL);
  console.log('💡 To use local backend, set REACT_APP_USE_LOCAL_BACKEND=true or run: npm run start:local');
}

const API = {
  // 🔐 Auth
  login: `${API_BASE_URL}/login`,
  signup: `${API_BASE_URL}/signup`,
  verifyEmail: `${API_BASE_URL}/verify-email`,
  resendVerification: `${API_BASE_URL}/verify-email/resend`,
  validateReferral: (code) => `${API_BASE_URL}/referrer/${code}`,
  
  // 🔑 Forgot Password
  forgotPassword: `${API_BASE_URL}/auth/forgot-password`,
  verifyResetCode: `${API_BASE_URL}/auth/verify-reset-code`,
  resetPassword: `${API_BASE_URL}/auth/reset-password`,

  // 👤 User / Profile
  userProfile: (id) => `${API_BASE_URL}/user/${id}`,   // GET /api/user/:id
  updateProfile: `${API_BASE_URL}/update-profile`,
  userInterests: `${API_BASE_URL}/user/interests`,
  videoCategories: `${API_BASE_URL}/video-categories`,

  // 🌍 Lookups
  countries: `${API_BASE_URL}/countries`,
  ethnicities: `${API_BASE_URL}/ethnicities`,
  sexes: `${API_BASE_URL}/sex`,
  regions: (countryId) => `${API_BASE_URL}/regions/${countryId}`,

  // 💰 Payments
  savedPaymentAccounts: `${API_BASE_URL}/saved-user-payment-accounts`,
  userPaymentAccounts: `${API_BASE_URL}/user-payment-accounts`,
  paymentMethods: `${API_BASE_URL}/payment-methods`, // Old endpoint (existing)
  
  // 💰 Payment Methods (New - Multi-currency system)
  availablePaymentMethods: `${API_BASE_URL}/payment-methods/available`, // Get methods for logged-in user (JWT-based)
  allPaymentMethods: `${API_BASE_URL}/payment-methods/all`, // Admin: Get all methods
  paymentMethodsByCountry: (countryCode) => `${API_BASE_URL}/payment-methods/country/${countryCode}`, // Public: Get methods for a country
  calculatePaymentFees: `${API_BASE_URL}/payment-methods/calculate-fees`, // Calculate fees for amount (POST)
  
  // 🌍 Payment Methods by Country (PaymentMethodCountries table)
  paymentMethodsForUser: `${API_BASE_URL}/payment-methods/by-user`, // Get available methods based on user's country
  paymentMethodsForCountryCode: (countryCode) => `${API_BASE_URL}/payment-methods/by-country/${countryCode}`, // Get available methods for specific country
  paymentMethodsCountrySummary: `${API_BASE_URL}/payment-methods/countries-summary`, // Admin: Summary of methods per country
  
  // 💸 Fee Calculation (Multi-currency)
  calculateFees: `${API_BASE_URL}/fees/calculate`, // GET or POST - Calculate withdrawal fees
  getUserFeeStructures: (userId) => `${API_BASE_URL}/fees/user/${userId}`, // Get all fee structures for user

  // 💸 Withdrawals
  withdrawalRequest: `${API_BASE_URL}/withdrawals/request`,
  withdrawalHistory: `${API_BASE_URL}/withdrawals/history`,

  // 🧾 Wallet Balance
  walletBalance: `${API_BASE_URL}/wallet/balance`, // JWT user’s own balance
  adminWalletBalance: (userId) => `${API_BASE_URL}/admin/wallet/balance/${userId}`, // Admin query by userId

  adminWithdrawals: `${API_BASE_URL}/admin/withdrawals`,
  adminPaymentMethods: `${API_BASE_URL}/admin/payment-methods`,
  adminVideoCategories: `${API_BASE_URL}/admin/video-categories`,
  adminCountryKYC: `${API_BASE_URL}/admin/country-kyc`,
  
  // 💰 JazzCash
  jazzCashExport: `${API_BASE_URL}/jazzcash/export`,
  jazzCashJobStatus: (jobExecutionId) => `${API_BASE_URL}/jazzcash/job-status/${jobExecutionId}`,

  // 🎥 Videos & Ads
  videoUpload: `${API_BASE_URL}/video-upload`,
  videosByCategory: `${API_BASE_URL}/videos/by-category`,
  userInterestVideos: `${API_BASE_URL}/user-interest-videos/by-user-interest`,
  userVideos: `${API_BASE_URL}/user/videos`,
  userVideoImpressions: `${API_BASE_URL}/user/video-impressions`,
  logAdEvents: `${API_BASE_URL}/ad-events`,
  
  // 🏷️ Categories
  categories: `${API_BASE_URL}/categories`,
  subcategoriesByCategory: (categoryId) => `${API_BASE_URL}/categories/${categoryId}/subcategories`,
  allSubcategories: `${API_BASE_URL}/categories/subcategories`,

  // 📺 Channels
  createChannel: `${API_BASE_URL}/channels`,
  myChannels: `${API_BASE_URL}/channels/my-channels`,
  allChannels: `${API_BASE_URL}/channels`,
  channelById: (channelId) => `${API_BASE_URL}/channels/${channelId}`,
  channelVideos: (channelId) => `${API_BASE_URL}/channels/${channelId}/videos`,
  updateChannel: (channelId) => `${API_BASE_URL}/channels/${channelId}`,
  deleteChannel: (channelId) => `${API_BASE_URL}/channels/${channelId}`,

  // 🎬 Video Management
  videoById: (videoId) => `${API_BASE_URL}/videos/${videoId}`,
  updateVideo: (videoId) => `${API_BASE_URL}/videos/${videoId}`,
  deleteVideo: (videoId) => `${API_BASE_URL}/videos/${videoId}`,
  toggleVideoActive: (videoId) => `${API_BASE_URL}/videos/${videoId}/toggle-active`,

  // 👥 Referrals
  referralsList: (id) => `${API_BASE_URL}/referrals/list/${id}`,
  referralsTree: (id) => `${API_BASE_URL}/referrals/tree/${id}`,
  directReferrals: (referralCode) => `${API_BASE_URL}/referrals/direct/${referralCode}`, // Lazy loading for tree view
  referralLink: (id) => `${API_BASE_URL}/referrals/${id}/link`,
  sendReferralInvites: `${API_BASE_URL}/referrals/invite`,
  getInviteStatus: `${API_BASE_URL}/referrals/invite-status`,
  getUsersByLevel: `${API_BASE_URL}/referrals/users-by-level`,
  getLevel1ReferralsForReassignment: `${API_BASE_URL}/referrals/level1-for-reassignment`,
  reassignLevel1Parent: `${API_BASE_URL}/referrals/reassign-parent`,
  getReferralReassignmentHistory: `${API_BASE_URL}/referrals/reassignment-history`,

  // 👨‍👩‍👧 Parent-Child Relationships
  requestParentChildRelationship: `${API_BASE_URL}/relationships/request`,
  getParentChildRequests: `${API_BASE_URL}/relationships/pending`,
  updateParentChildRelationshipStatus: (id) => `${API_BASE_URL}/relationships/${id}/status`,
  getParentChildRelationships: `${API_BASE_URL}/relationships`,
  createParentChildWithdrawal: `${API_BASE_URL}/relationships/withdraw`,
  getParentChildWithdrawalHistory: `${API_BASE_URL}/relationships/withdrawal-history`,

  // 👨‍💼 Admin
  adminUsers: `${API_BASE_URL}/admin/users`,
  adminUserDetails: (id) => `${API_BASE_URL}/admin/users/${id}`,
  adminUserFlags: (id) => `${API_BASE_URL}/admin/users/${id}/flags`,
  adminUserResetPassword: (id) => `${API_BASE_URL}/admin/users/${id}/reset-password`,
  adminRoles: `${API_BASE_URL}/admin/roles`,
  adminVideos: `${API_BASE_URL}/admin/videos`,
  adminVideoStats: `${API_BASE_URL}/admin/videos/stats`,

  // Admin: Ad Monetization
  adProviders: `${API_BASE_URL}/admin/ad-providers`,
  adminAdPayments: `${API_BASE_URL}/admin/ad-payments`,
  adminAdApps: `${API_BASE_URL}/admin/ad-payments/apps`,
  adminAdProviderCountryRevenue: `${API_BASE_URL}/admin/ad-payments/country-revenue`,
  adminConversions: `${API_BASE_URL}/admin/conversions`,
  adminConversionSummary: `${API_BASE_URL}/admin/conversions/summary`,

  // Admin: Tokens & Rewards
  adminTokenLedger: `${API_BASE_URL}/admin/tokens/ledger`,
  adminTokenSummary: `${API_BASE_URL}/admin/tokens/summary`,
  adminTokenPending: `${API_BASE_URL}/admin/tokens/pending`,
  adminUpdateRewardStatus: (rewardId) =>
    `${API_BASE_URL}/admin/tokens/rewards/${rewardId}/status`,
  
  // Admin: Pending Clicks Management
  adminPendingClicksDiagnose: `${API_BASE_URL}/admin/tokens/pending-clicks/diagnose`,
  adminPendingClicksProcess: `${API_BASE_URL}/admin/tokens/pending-clicks/process`,

  // Earnings
  earningsSummary: `${API_BASE_URL}/earnings/summary`,
  tokenLedger: `${API_BASE_URL}/earnings/ledger`,

  // 🌍 App Launch Regions (Admin)
  adminAppRegions: `${API_BASE_URL}/admin/app-regions`,
  adminAppRegionsToggle: `${API_BASE_URL}/admin/app-regions/set`,

  // 🔐 KYC (Multi-Provider)
  kycProviders: `${API_BASE_URL}/kyc/providers`,
  kycStatus: `${API_BASE_URL}/kyc/status`,
  kycStart: `${API_BASE_URL}/kyc/start`,
  kycUpdateStatus: `${API_BASE_URL}/kyc/update-status`,
  kycRequirementByCountry: `${API_BASE_URL}/admin/country-kyc/country`, // Get KYC requirement for a specific country
  
  // Stripe-specific KYC
  stripeCreateSession: `${API_BASE_URL}/kyc/stripe/session`,
  stripeCreateAccountLink: `${API_BASE_URL}/kyc/stripe/account-link`,
  stripeWebhook: `${API_BASE_URL}/kyc/stripe/webhook`,

  // 💳 Payment Settings & History
  paymentSettings: `${API_BASE_URL}/payment-settings`,
  paymentSettingsEligibility: `${API_BASE_URL}/payment-settings/eligibility`,
  acceptPaymentTerms: `${API_BASE_URL}/payment-settings/accept-terms`,
  paymentHistory: `${API_BASE_URL}/payment-history`,
  nextPayment: `${API_BASE_URL}/payment-history/next`,
  paymentSchedule: `${API_BASE_URL}/payment-history/schedule`,

  // 💸 PayPal Integration
  paypalTestConnection: `${API_BASE_URL}/paypal/test-connection`,
  paypalPayout: `${API_BASE_URL}/paypal/payout`,
  paypalBatchPayout: `${API_BASE_URL}/paypal/batch-payout`,
  paypalPayoutStatus: (batchId) => `${API_BASE_URL}/paypal/payout-status/${batchId}`,
  paypalPayoutItem: (itemId) => `${API_BASE_URL}/paypal/payout-item/${itemId}`,

  // 💳 Stripe Payout Integration
  stripeTestConnection: `${API_BASE_URL}/stripe/test-connection`,
  stripePayout: `${API_BASE_URL}/stripe/payout`,
  stripeTransferDetails: (transferId) => `${API_BASE_URL}/stripe/transfer/${transferId}`,

  // 💎 USDT Integration
  usdtTestConnection: `${API_BASE_URL}/usdt/test-connection`,
  usdtValidateAddress: `${API_BASE_URL}/usdt/validate-address`,
  usdtPayout: `${API_BASE_URL}/usdt/payout`,
  usdtAutomatedPayout: `${API_BASE_URL}/usdt/automated-payout`,
  usdtPending: `${API_BASE_URL}/usdt/pending`,
  usdtTransaction: (txHash) => `${API_BASE_URL}/usdt/transaction/${txHash}`,

  // 🤖 Admin Withdrawal Management (PayPal, Stripe & USDT)
  adminWithdrawalStats: `${API_BASE_URL}/admin/withdrawals/stats`,
  adminWithdrawalsPending: `${API_BASE_URL}/admin/withdrawals/pending`,
  adminTriggerAutoProcess: `${API_BASE_URL}/admin/withdrawals/trigger-auto-process`,
  adminWithdrawalConfig: `${API_BASE_URL}/admin/withdrawals/config`,
  adminApproveWithdrawal: (id) => `${API_BASE_URL}/admin/withdrawals/${id}/approve`,
  adminRejectWithdrawal: (id) => `${API_BASE_URL}/admin/withdrawals/${id}/reject`,
  adminBusinessRules: `${API_BASE_URL}/admin/withdrawals/business-rules`,

  // Base URL for dynamic usage
  baseUrl: API_BASE_URL.replace('/api', ''),
};

// Optional: admin contact info
const adminEmail = "admin@example.com";
const adminPhone = "+44123456789";

export { API, API_BASE_URL, adminEmail, adminPhone };
